// Maintenance — track roaster cleaning + threshold-driven alerts.
//
// Three-section layout:
//   1. Status cards (one per maintenance type) showing ok/due/overdue/unknown
//   2. "Log maintenance" form (collapsed by default; expand inline)
//   3. History list (most-recent-first)
//
// Defaults match the Aillio Bullet R2 Pro Operation Manual:
//   routine    every 5 roasts / 3 kg
//   regular    every 60 roasts / 30 kg
//   annual     every 365 days
//   ibts_lens  as-needed (sensor-driven via FC IBTS drift)
//
// The page reads from get_maintenance_status (which auto-detects
// IBTS lens drift) and writes via log_maintenance.

import { useEffect, useState } from "react";
import { useApi } from "../useApi";
import type {
  MaintenanceEvent,
  MaintenanceState,
  MaintenanceStatusResponse,
  MaintenanceTypeStatus,
} from "../types";
import { ErrorBox } from "../components/ErrorBox";
import { formatDate } from "../format";

const DEFAULT_TYPES = ["routine", "regular", "annual", "ibts_lens"] as const;

// Operator-facing labels — the manual's section names. Tooltip-style
// helper text comes from the manual entry for each.
const TYPE_LABELS: Record<string, { name: string; help: string }> = {
  routine: {
    name: "Routine",
    help: "Chaff collector + chaff filter + cooling tray. Manual: every session or 2-3 kg.",
  },
  regular: {
    name: "Regular",
    help: "Impeller + Pro cooling tray + vacuum under drum. Manual: every 30 kg.",
  },
  annual: {
    name: "Annual",
    help: "Exhaust pipe + drum removal + IBTS PCB inspection. Manual: yearly.",
  },
  ibts_lens: {
    name: "IBTS lens",
    help: "Wipe with isopropyl + cotton swab. Manual: when FC IBTS drifts < 195°C.",
  },
};

export function Maintenance() {
  const api = useApi();
  const [status, setStatus] = useState<MaintenanceStatusResponse | null>(null);
  const [history, setHistory] = useState<MaintenanceEvent[]>([]);
  const [err, setErr] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);

  // Form state.
  const [showForm, setShowForm] = useState(false);
  const [formType, setFormType] = useState<string>("routine");
  const [formDate, setFormDate] = useState<string>(
    new Date().toISOString().slice(0, 10),
  );
  const [formNotes, setFormNotes] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);

  const refresh = async () => {
    setLoading(true);
    setErr(null);
    try {
      const [s, h] = await Promise.all([
        api.getMaintenanceStatus(),
        api.listMaintenanceHistory(50),
      ]);
      setStatus(s);
      setHistory(h);
    } catch (e) {
      setErr(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
  }, [api]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setErr(null);
    try {
      await api.logMaintenance({
        maintenance_type: formType,
        performed_at: formDate || null,
        notes: formNotes,
      });
      // Reset form, refresh data.
      setFormNotes("");
      setShowForm(false);
      await refresh();
    } catch (e) {
      setErr(e);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <div className="row" style={{ justifyContent: "space-between", alignItems: "baseline" }}>
        <h1 style={{ marginTop: 0 }}>Maintenance</h1>
        <button onClick={() => setShowForm((v) => !v)}>
          {showForm ? "Cancel" : "Log maintenance"}
        </button>
      </div>

      <ErrorBox error={err} />

      {showForm && (
        <form onSubmit={submit} className="card" style={{ marginBottom: 24 }}>
          <div className="filter-bar" style={{ marginBottom: 0 }}>
            <label>
              Type
              <select
                value={formType}
                onChange={(e) => setFormType(e.target.value)}
              >
                {DEFAULT_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {TYPE_LABELS[t].name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Date
              <input
                type="date"
                value={formDate}
                onChange={(e) => setFormDate(e.target.value)}
                required
              />
            </label>
            <label style={{ flex: 1 }}>
              Notes
              <input
                value={formNotes}
                onChange={(e) => setFormNotes(e.target.value)}
                placeholder="optional"
              />
            </label>
            <div style={{ alignSelf: "end" }}>
              <button type="submit" disabled={submitting}>
                {submitting ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
          <p className="muted small" style={{ marginTop: 8 }}>
            {TYPE_LABELS[formType]?.help}
          </p>
        </form>
      )}

      {/* Status cards */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
          gap: 12,
          marginBottom: 24,
        }}
      >
        {status?.statuses.map((s) => <MaintenanceCard key={s.maintenance_type} s={s} />)}
        {loading && !status && (
          <div className="muted">Loading status…</div>
        )}
      </div>

      {/* History */}
      <h2>History</h2>
      {history.length === 0 ? (
        <p className="muted">
          No maintenance logged yet. Click "Log maintenance" above to record
          your first event.
        </p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Type</th>
              <th>Since previous</th>
              <th>Notes</th>
            </tr>
          </thead>
          <tbody>
            {history.map((e) => (
              <tr key={e.event_id}>
                <td>{formatDate(e.performed_at)}</td>
                <td>
                  {TYPE_LABELS[e.maintenance_type]?.name ?? e.maintenance_type}
                </td>
                <td className="muted small">
                  {e.roasts_since_previous != null
                    ? `${e.roasts_since_previous} roasts / ${
                        ((e.grams_since_previous ?? 0) / 1000).toFixed(1)
                      } kg`
                    : "—"}
                </td>
                <td className="small">{e.notes || <span className="muted">—</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// --- Card component -------------------------------------------------------

function MaintenanceCard({ s }: { s: MaintenanceTypeStatus }) {
  const label = TYPE_LABELS[s.maintenance_type] ?? {
    name: s.maintenance_type,
    help: "",
  };
  const cls = stateClass(s.state);
  return (
    <div className="card" style={{ borderLeft: `4px solid ${stateColor(s.state)}` }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <strong>{label.name}</strong>
        <span className={`badge ${cls}`}>{s.state.toUpperCase()}</span>
      </div>
      <p className="muted small" style={{ marginTop: 6, marginBottom: 6 }}>
        {label.help}
      </p>
      <div className="small">{s.message}</div>
      {s.last_performed_at && (
        <div className="muted small" style={{ marginTop: 6 }}>
          Last: {formatDate(s.last_performed_at)}
          {s.days_since != null && ` · ${s.days_since}d ago`}
        </div>
      )}
    </div>
  );
}

function stateClass(state: MaintenanceState): "ok" | "warn" | "err" | "" {
  switch (state) {
    case "ok": return "ok";
    case "due": return "warn";
    case "overdue": return "err";
    default: return "";
  }
}

function stateColor(state: MaintenanceState): string {
  switch (state) {
    case "ok": return "var(--color-success, #2a8c4a)";
    case "due": return "var(--color-warn, #c47100)";
    case "overdue": return "var(--color-danger, #b53d3d)";
    default: return "var(--color-border, #c0c0c0)";
  }
}
